import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const PrivacyPolicyPage = () => {
  const { language } = useLanguage();

  const content = {
    es: {
      title: 'Política de Privacidad',
      lastUpdated: 'Última actualización: Marzo 2025',
      backHome: 'Volver al inicio',
      sections: [
        {
          title: '1. Introducción',
          content: `En AJ Code Labs ("nosotros", "nuestro" o "la Empresa"), respetamos su privacidad y nos comprometemos a proteger sus datos personales. Esta política de privacidad explica cómo recopilamos, usamos, almacenamos y protegemos su información cuando utiliza nuestro sitio web ajcodelabs.ai y nuestros servicios.`
        },
        {
          title: '2. Información que Recopilamos',
          content: `Recopilamos los siguientes tipos de información:

**Información proporcionada directamente:**
• Nombre completo
• Dirección de correo electrónico
• Número de teléfono/WhatsApp
• Nombre de la empresa (opcional)
• Tipo de proyecto de interés
• Mensajes y consultas que nos envía

**Información recopilada automáticamente:**
• Dirección IP
• Tipo de navegador y dispositivo
• Páginas visitadas y tiempo de permanencia
• Datos de cookies y tecnologías similares`
        },
        {
          title: '3. Cómo Usamos su Información',
          content: `Utilizamos su información para:

• Responder a sus consultas y solicitudes de cotización
• Proporcionar nuestros servicios de desarrollo de software, implementación de IA y digitalización
• Enviar comunicaciones relacionadas con nuestros servicios (si se ha suscrito)
• Mejorar nuestro sitio web y servicios
• Cumplir con obligaciones legales
• Analizar el uso del sitio mediante herramientas como Google Analytics`
        },
        {
          title: '4. Base Legal para el Procesamiento',
          content: `Procesamos sus datos personales basándonos en:

• **Consentimiento:** Cuando nos proporciona sus datos a través de formularios
• **Interés legítimo:** Para mejorar nuestros servicios y comunicarnos con clientes potenciales
• **Obligación legal:** Cuando la ley lo requiere`
        },
        {
          title: '5. Compartir Información',
          content: `No vendemos ni alquilamos su información personal. Podemos compartir datos con:

• **Proveedores de servicios:** Que nos ayudan a operar (hosting, email, análisis)
• **Autoridades legales:** Cuando la ley lo requiere
• **Socios comerciales:** Solo con su consentimiento explícito

Nuestros proveedores actuales incluyen:
• Resend (envío de emails)
• Google Analytics (análisis web)
• MongoDB (almacenamiento de datos)`
        },
        {
          title: '6. Seguridad de los Datos',
          content: `Implementamos medidas de seguridad técnicas y organizativas para proteger sus datos:

• Cifrado SSL/TLS para todas las transmisiones
• Acceso restringido a datos personales
• Servidores seguros y actualizados
• Revisiones periódicas de seguridad

Sin embargo, ningún método de transmisión por Internet es 100% seguro.`
        },
        {
          title: '7. Retención de Datos',
          content: `Conservamos sus datos personales mientras:

• Exista una relación comercial activa
• Sea necesario para cumplir con obligaciones legales
• Usted no solicite su eliminación

Los datos de contacto se conservan por un máximo de 3 años desde la última interacción.`
        },
        {
          title: '8. Sus Derechos',
          content: `Usted tiene derecho a:

• **Acceso:** Solicitar una copia de sus datos
• **Rectificación:** Corregir datos inexactos
• **Eliminación:** Solicitar que eliminemos sus datos
• **Portabilidad:** Recibir sus datos en formato estructurado
• **Oposición:** Oponerse al procesamiento de sus datos
• **Retiro del consentimiento:** En cualquier momento

Para ejercer estos derechos, contáctenos en: contacto@ajcodelabs.ai`
        },
        {
          title: '9. Cookies',
          content: `Nuestro sitio utiliza cookies para:

• Recordar sus preferencias (idioma)
• Analizar el tráfico del sitio
• Mejorar la experiencia de usuario

Puede configurar su navegador para rechazar cookies, aunque algunas funciones del sitio podrían verse afectadas.`
        },
        {
          title: '10. Enlaces a Terceros',
          content: `Nuestro sitio puede contener enlaces a sitios de terceros. No somos responsables de las prácticas de privacidad de estos sitios. Le recomendamos revisar sus políticas de privacidad.`
        },
        {
          title: '11. Cambios a esta Política',
          content: `Podemos actualizar esta política periódicamente. Los cambios serán publicados en esta página con la fecha de actualización. Le recomendamos revisar esta política regularmente.`
        },
        {
          title: '12. Contacto',
          content: `Si tiene preguntas sobre esta política de privacidad, contáctenos:

**AJ Code Labs**
Email: contacto@ajcodelabs.ai

Oficinas:
• Miami, FL - Estados Unidos
• Quito - Ecuador
• Londres - Reino Unido`
        }
      ]
    },
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: March 2025',
      backHome: 'Back to home',
      sections: [
        {
          title: '1. Introduction',
          content: `At AJ Code Labs ("we", "our" or "the Company"), we respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, store, and protect your information when you use our website ajcodelabs.ai and our services.`
        },
        {
          title: '2. Information We Collect',
          content: `We collect the following types of information:

**Information you provide directly:**
• Full name
• Email address
• Phone number/WhatsApp
• Company name (optional)
• Type of project of interest
• Messages and inquiries you send us

**Information collected automatically:**
• IP address
• Browser and device type
• Pages visited and time spent
• Cookies and similar technologies data`
        },
        {
          title: '3. How We Use Your Information',
          content: `We use your information to:

• Respond to your inquiries and quote requests
• Provide our software development, AI implementation, and digitalization services
• Send communications related to our services (if you have subscribed)
• Improve our website and services
• Comply with legal obligations
• Analyze site usage through tools like Google Analytics`
        },
        {
          title: '4. Legal Basis for Processing',
          content: `We process your personal data based on:

• **Consent:** When you provide your data through forms
• **Legitimate interest:** To improve our services and communicate with potential clients
• **Legal obligation:** When required by law`
        },
        {
          title: '5. Information Sharing',
          content: `We do not sell or rent your personal information. We may share data with:

• **Service providers:** Who help us operate (hosting, email, analytics)
• **Legal authorities:** When required by law
• **Business partners:** Only with your explicit consent

Our current providers include:
• Resend (email delivery)
• Google Analytics (web analytics)
• MongoDB (data storage)`
        },
        {
          title: '6. Data Security',
          content: `We implement technical and organizational security measures to protect your data:

• SSL/TLS encryption for all transmissions
• Restricted access to personal data
• Secure and updated servers
• Regular security reviews

However, no method of Internet transmission is 100% secure.`
        },
        {
          title: '7. Data Retention',
          content: `We retain your personal data while:

• There is an active business relationship
• It is necessary to comply with legal obligations
• You do not request its deletion

Contact data is retained for a maximum of 3 years from the last interaction.`
        },
        {
          title: '8. Your Rights',
          content: `You have the right to:

• **Access:** Request a copy of your data
• **Rectification:** Correct inaccurate data
• **Erasure:** Request that we delete your data
• **Portability:** Receive your data in a structured format
• **Objection:** Object to the processing of your data
• **Withdraw consent:** At any time

To exercise these rights, contact us at: contacto@ajcodelabs.ai`
        },
        {
          title: '9. Cookies',
          content: `Our site uses cookies to:

• Remember your preferences (language)
• Analyze site traffic
• Improve user experience

You can configure your browser to reject cookies, although some site features may be affected.`
        },
        {
          title: '10. Third-Party Links',
          content: `Our site may contain links to third-party sites. We are not responsible for the privacy practices of these sites. We recommend reviewing their privacy policies.`
        },
        {
          title: '11. Changes to This Policy',
          content: `We may update this policy periodically. Changes will be posted on this page with the update date. We recommend reviewing this policy regularly.`
        },
        {
          title: '12. Contact',
          content: `If you have questions about this privacy policy, contact us:

**AJ Code Labs**
Email: contacto@ajcodelabs.ai

Offices:
• Miami, FL - United States
• Quito - Ecuador
• London - United Kingdom`
        }
      ]
    }
  };

  const t = content[language];

  return (
    <main className="min-h-screen bg-[#0A0A0B]">
      <SEO 
        title={t.title}
        description={language === 'es' 
          ? 'Política de privacidad de AJ Code Labs. Conoce cómo protegemos y usamos tus datos personales.'
          : 'AJ Code Labs privacy policy. Learn how we protect and use your personal data.'}
        url="/privacy"
      />
      
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden bg-[#FAFAFA]">
        <div className="container-custom relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#E03A1C] transition-colors mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            {t.backHome}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4 mb-6"
          >
            <div className="p-3 bg-[#E03A1C] rounded-xl">
              <Shield className="w-8 h-8 text-[#FAFAFA]" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#0A0A0B] tracking-tight">
                {t.title}
              </h1>
              <p className="text-[#52525B] mt-2">{t.lastUpdated}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 bg-[#FAFAFA]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white rounded-2xl p-8 md:p-12 border border-[#E4E4E7]">
              {t.sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={index !== t.sections.length - 1 ? 'mb-10 pb-10 border-b border-[#E4E4E7]' : ''}
                >
                  <h2 className="text-xl md:text-2xl font-semibold text-[#0A0A0B] mb-4 tracking-tight">
                    {section.title}
                  </h2>
                  <div className="text-[#52525B] leading-relaxed whitespace-pre-line">
                    {section.content.split('**').map((part, i) => 
                      i % 2 === 1 ? (
                        <strong key={i} className="text-[#0A0A0B] font-semibold">{part}</strong>
                      ) : (
                        <span key={i}>{part}</span>
                      )
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default PrivacyPolicyPage;
